// js/components/ProductSection.js
import Delegate from 'ftdomdelegate';
import { ProductGallery } from 'product-gallery';
import { ShippingEstimator } from 'shipping-estimator';
import { QuantityPicker } from 'quantity-picker';
import { Listeners, SecureStorage } from 'utils';

export class ProductSection extends HTMLElement {
  constructor() {
    super();
    this.delegateElement = new Delegate(this);
    const settingsAttr =
      this.getAttribute("data-section-settings") || this.getAttribute("data-settings") || "{}";

    try {
      this.options = JSON.parse(settingsAttr) || {};
    } catch (error) {
      console.error("Invalid product section settings JSON:", error);
      this.options = {};
    }
  }
  connectedCallback() {
    this.productGallery = new ProductGallery(this, this.options);

    // Guard payment button init behind Shopify.designMode: Apple Pay / GPay
    // surfaces a browser payment-method popup that nags editors on every section
    // reload. Skip entirely in the theme editor.
    // (skill: cos-theme/theme-editor-events)
    if (!Shopify.designMode) {
      if (this.options["isQuickView"] && window.Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }
    }

    if (this.options["showShippingEstimator"]) {
      this.shippingEstimator = new ShippingEstimator(this);
    }

    // Save recently viewed product — skip in design mode to avoid polluting
    // storage with editor-context navigations.
    if (!Shopify.designMode) {
      this._saveRecentlyViewedProduct();
    }

    this._attachListeners();

    // Theme editor: re-init handler so section reload in the editor reconnects
    // gallery and delegate listeners without requiring a full page reload.
    // (skill: cos-theme/theme-editor-events)
    this._handleSectionLoad = this._onSectionLoad.bind(this);
    document.addEventListener('shopify:section:load', this._handleSectionLoad);
  }
  disconnectedCallback() {
    this.delegateElement.off();
    if (this.productGallery) {
      this.productGallery.destroy();
    }
    if (this.shippingEstimator) {
      this.shippingEstimator.destroy();
    }

    // Cleanup sticky form observer
    if (this._stickyObserver) {
      this._stickyObserver.disconnect();
    }

    if (this._handleSectionLoad) {
      document.removeEventListener('shopify:section:load', this._handleSectionLoad);
    }
  }
  onUnload() {
    this.disconnectedCallback();
  }
  _onSectionLoad(event) {
    // Called by the theme editor when this section is reloaded. Re-init the
    // gallery and re-attach variant listeners so the product page stays
    // interactive after editor changes.
    if (event.target.contains(this)) {
      if (this.productGallery) this.productGallery.destroy();
      this.productGallery = new ProductGallery(this, this.options);
      this.delegateElement.off();
      this._attachListeners();
    }
  }
  _attachListeners() {
    this._onVariantChangedListener = this._onVariantChanged.bind(this);
    this.addEventListener("variant:change", this._onVariantChangedListener);

    // Sticky form functionality with Intersection Observer
    this._initStickyFormObserver();
  }
  /**
   * This method is called when the variant is changed due to option
   */
  _onVariantChanged(event) {
    this.productGallery.variantHasChanged(event.detail.variant);
  }

  _initStickyFormObserver() {
    const stickyWrapper = document.querySelector('.sticky__product-info-wrapper');
    const buyButton = document.querySelector('.product-form__add-button');

    if (!stickyWrapper || !buyButton) return;

    // Get the initial position of the buy button
    const buyButtonRect = buyButton.getBoundingClientRect();
    const initialButtonTop = buyButtonRect.top + window.scrollY;

    // Create intersection observer
    this._stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Buy button is visible - hide sticky form
          stickyWrapper.classList.remove('is-visible');
        } else {
          // Buy button is not visible - check if user has scrolled past initial position
          const currentScrollY = window.scrollY;
          if (currentScrollY > initialButtonTop) {
            // User has scrolled past the initial button position - show sticky form
            stickyWrapper.classList.add('is-visible');
          } else {
            // User is at the top of the page - hide sticky form
            stickyWrapper.classList.remove('is-visible');
          }
        }
      });
    }, {
      root: null, // Use viewport as root
      rootMargin: '0px', // No margin
      threshold: 0.1 // Trigger when 10% of the button is visible
    });

    // Start observing the buy button
    this._stickyObserver.observe(buyButton);
  }

  /**
   * Save the current product to recently viewed products
   */
  _saveRecentlyViewedProduct() {
    const productId = this.options?.productId || window.theme?.productId;
    if (!productId) return;

    // Get existing items from storage
    let items = SecureStorage.getJSON('recentlyViewedProducts', []);

    // If the product already exists, remove it first
    if (items.includes(productId)) {
      items.splice(items.indexOf(productId), 1);
    }

    // Add current product to the beginning
    items.unshift(productId);

    // Save back to storage, keeping only the 18 most recent
    SecureStorage.setJSON('recentlyViewedProducts', items.slice(0, 18));
  }
}

class CosPhoneField extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.Listeners = new Listeners();
    this.select = this.querySelector('[data-prefix-select]');
    this.label = this.querySelector('[data-prefix-label]');
    this.labelText = this.querySelector('[data-prefix-label-text]');
    this.phone = this.querySelector('input[type="tel"]');
    
    // Event listeners
    this.Listeners.add(this.select, 'change', () => this.validate());
    this.Listeners.add(this.phone, 'change', () => this.validatePhone());
  }

  disconnectedCallback() {
    if (this.Listeners) {
      this.Listeners.removeAll();
    }
  }

  validatePhone() {
    // Validează și corectează numărul de telefon
    let phone = this.phone.value;
    
    // Caută prefixul în lista de opțiuni
    for (let i = 0; i < this.select.options.length; i++) {
      if (phone.startsWith(this.select.options.item(i).value)) {
        this.select.selectedIndex = i;
      }
    }
    

    this.labelText.innerText = this.select.options[this.select.selectedIndex].dataset.countryShortName;
    return phone;
  }

  correctPhone() {
    // Corectează formatul numărului de telefon
    let oldPrefix = this.phone.getAttribute('data-prefix');
    const prefix = this.select.value;
    let phone = this.phone.value.trim();
    
    // Adaugă prefixul dacă nu îl are
    if (!phone.startsWith(prefix, 0)) {
      // phone = prefix + " " + phone;
      phone = prefix + " " + phone;
    }

    // Scoate prefixul dacă îl are
    // if (phone.startsWith(prefix, 0)) {
    //   phone = phone.replace(prefix, '');
    // }
    
    this.phone.setAttribute('data-prefix', prefix);
    return phone;
  }

  validate() {
    this.phone.value = this.correctPhone();
    this.labelText.innerText = this.select.options[this.select.selectedIndex].dataset.countryShortName;
    return this.phone.value;
  }
}

if (!window.customElements.get("cos-product")) window.customElements.define("cos-product", ProductSection);
if (!window.customElements.get("quantity-picker")) window.customElements.define("quantity-picker", QuantityPicker);
if (!window.customElements.get("cos-phone-field")) window.customElements.define("cos-phone-field", CosPhoneField);
